import { isEmpty } from 'lodash';
import { batch } from 'react-redux';
import { EmeraldError, EmeraldErrorModal } from 'src/common/api/emerald-error';
import { showEmeraldDialog } from 'src/common/components/interactive-dialogue/components/interactive-dialog-content/interactive-dialog-content';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import { popEmeraldModal } from 'src/common/emerald-modal-manager/emerald-modal-manager';
import { IMessageModal, MessageModalSizeEnum } from 'src/common/interfaces/dtos/IMessageModal';
import { swipeAhead } from 'src/devices/payment/get-card-details';
import {
  beginPaymentSession,
  enablePayments,
  inPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { applyCartResponse } from 'src/home-page/business-logic/common/apply-cart-response';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import {
  CartCustomerData,
  CartResponse,
} from 'src/items-list/business-logic/service/interfaces/cart-response';
import { EmeraldDialogTypes } from '../../common/enumerations/emerald-dialog-types';
import {
  CartReference,
  getCustomer,
  ISavedTransaction,
  resumeSavedTransaction,
  updateRecallCartStatus,
} from '../../common/helpers/transaction-helper';
import { store } from '../../common/redux/configure-store';
import { TransactionActions } from '../../common/redux/reducers/transaction-reducer';
import { getInterventions } from '../../interventions/business-logic/get-interventions';

export const recallTransaction = async (
  transactionId: string,
  isBarCode: boolean,
  touchpointId?: string,
  cartReference?: CartReference,
): Promise<void> => {
  if (store.getState().transaction.transactionIdExists) {
    const error: IMessageModal = {
      message: 'ActiveTransactionExists',
      type: EmeraldDialogTypes.Error,
      subMessage: 'VoidTransactionFirst',
    };
    throw error;
  }
  const transactionParms: ISavedTransaction = {
    messageId: 0,
    transactionId,
    isBarCode,
    tillId: touchpointId,
    cartReference,
  };
  const { recallUpdateCartStatus } = getCurrentXLRConfig();
  await withSpinner(() =>
    resumeSavedTransaction(transactionParms)
      .then(
        async (cartResponse: CartResponse): Promise<CartResponse> => {
          if (recallUpdateCartStatus) {
            store.dispatch(
              TransactionActions.setTransactionState({
                transactionId: cartResponse.cartId ?? '',
                sequenceNumber: cartResponse.sequenceNumber ?? 0,
              }),
            );
            transactionParms.cartStatus = CartStatus.Recall;
            await updateRecallCartStatus(transactionParms);
          }
          // TODO: we want to move this to itemized upon receiving the cart in recalled state. (after interventions are done)
          applyCartResponse(cartResponse);
          if (store.getState().pinPad.isPinpadConfigured) {
            const balanceDue = cartResponse.cartInfo?.totals?.balanceDue;
            await enablePayments();
            if (balanceDue) {
              if (!inPaymentSession()) {
                await beginPaymentSession();
              }
              swipeAhead();
            }
          }
          batch((): void => {
            store.dispatch(
              TransactionActions.setTransactionState({
                transactionId: cartResponse.cartId ?? '',
                sequenceNumber: cartResponse.sequenceNumber ?? 0,
                cartStatus: CartStatus.Recall,
              }),
            );
          });

          return cartResponse;
        },
      )
      .then(async (cartResponse: CartResponse) => {
        await findTransactionCustomer(cartResponse, transactionId);
      })
      .catch(err => {
        store.dispatch(
          TransactionActions.setTransactionState({
            transactionId: '',
            sequenceNumber: 0,
          }),
        );
        if (err.code === 'TraningModeIndicationIncompatibilityInSaveCart') {
          throw resumeTransactionErrorModal(err);
        } else {
          throw err;
        }
      }),
  );

  await getInterventions().catch(() => {});
};

const resumeTransactionErrorModal = (err: EmeraldError): EmeraldErrorModal => {
  return new EmeraldErrorModal({
    message: 'TransactionNotFound',
    subMessage: err.message,
    type: EmeraldDialogTypes.Error,
    size: MessageModalSizeEnum.Medium,
    acceptButtonLabel: 'OK',
    hideCancelButton: true,
    disableBackdropClick: true,
    dialogHeight: 54.427,
  });
};

const findTransactionCustomer = async (
  cartResponse: CartResponse,
  transactionId: string,
): Promise<void> => {
  try {
    const { getCustomerFromCartResponse } = getCurrentXLRConfig();

    const customer: CartCustomerData | undefined = getCustomerFromCartResponse
      ? cartResponse.customer
      : await getCustomer(transactionId);

    if (customer && !isEmpty(customer)) {
      store.dispatch(
        TransactionActions.setCustomer({
          points: customer.points,
          consumerAccountNumber: customer.consumerAccountNumber,
          firstName: customer.firstName,
          lastName: customer.lastName,
          emailAddress: customer.emailAddress,
          mobilePhoneNumber: customer.mobilePhoneNumber,
          programBalances: customer.programBalances,
        }),
      );
    }
  } catch (error) {
    const { alertWhenCustomerNotFound } = getCurrentXLRConfig();

    if (alertWhenCustomerNotFound) {
      showEmeraldDialog({
        message: 'TitleNoLoyaltyCustomerFound',
        subMessage: 'MessageAddCustomerToTransaction',
        type: EmeraldDialogTypes.Error,
        size: MessageModalSizeEnum.Medium,
        acceptButtonLabel: 'OK',
        onAccept: () => popEmeraldModal(),
        hideCancelButton: true,
        dialogHeight: 54.427,
      });
    }
  }
};
